import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../supabaseClient';

const BetaContext = createContext({});

export const useBeta = () => {
  const context = useContext(BetaContext);
  if (!context) {
    throw new Error('useBeta must be used within BetaProvider');
  }
  return context;
};

/**
 * BetaProvider - Beta 模式狀態管理
 *
 * 透過 project_profiles.settings JSONB 存取 beta_mode 狀態
 * 沿用 AuthContext 中 saveViewingContextToDB 的模式
 */
export const BetaProvider = ({ children }) => {
  const { profile, user, patchProfileSettings } = useAuth();
  const [isBetaMode, setIsBetaMode] = useState(true);
  const [betaLoading, setBetaLoading] = useState(true);

  // 從 profile.settings 讀取 beta_mode（預設為 true，Beta 模式）
  // 必須等 profile 載入後才能判斷，否則 betaLoading 過早 false 會導致
  // MainLayout 短暫渲染經典 layout，觸發不必要的 DataContext API 呼叫
  useEffect(() => {
    if (!profile) return;
    setIsBetaMode(profile?.settings?.beta_mode !== false);
    setBetaLoading(false);
  }, [profile]);

  // 切換 beta 模式，同步寫入 DB
  const toggleBetaMode = useCallback(async () => {
    if (!profile?.id) return;

    const newMode = !isBetaMode;
    setIsBetaMode(newMode);

    try {
      // 讀取當前 settings
      const { data: currentProfile, error: fetchError } = await supabase
        .from('project_profiles')
        .select('settings')
        .eq('user_id', profile.id)
        .single();

      if (fetchError) {
        console.error('Failed to read profile settings:', fetchError);
        return;
      }

      const currentSettings = currentProfile?.settings || {};
      const newSettings = {
        ...currentSettings,
        beta_mode: newMode,
      };

      // 首次進入 beta，記錄 beta_first_seen
      if (newMode && !currentSettings.beta_first_seen) {
        newSettings.beta_first_seen = new Date().toISOString();
      }

      // 更新到 DB
      const { error: updateError } = await supabase
        .from('project_profiles')
        .update({ settings: newSettings })
        .eq('user_id', profile.id);

      if (updateError) {
        console.error('Failed to update beta_mode:', updateError);
        // Revert on error
        setIsBetaMode(!newMode);
      }
    } catch (error) {
      console.error('Error toggling beta mode:', error);
      setIsBetaMode(!newMode);
    }
  }, [isBetaMode, profile?.id]);

  // 更新 beta settings（通用方法）
  const updateBetaSetting = useCallback(async (key, value) => {
    if (!profile?.id) return;

    try {
      const { data: currentProfile, error: fetchError } = await supabase
        .from('project_profiles')
        .select('settings')
        .eq('user_id', profile.id)
        .single();

      if (fetchError) {
        console.error('Failed to read profile settings:', fetchError);
        return;
      }

      const currentSettings = currentProfile?.settings || {};
      const newSettings = {
        ...currentSettings,
        [key]: value,
      };

      const { error: updateError } = await supabase
        .from('project_profiles')
        .update({ settings: newSettings })
        .eq('user_id', profile.id);

      if (updateError) {
        console.error('Failed to update beta setting:', updateError);
      } else {
        // 同步更新本地 profile.settings，避免 unmount/remount 時讀到舊值
        patchProfileSettings({ [key]: value });
      }
    } catch (error) {
      console.error('Error updating beta setting:', error);
    }
  }, [profile?.id, patchProfileSettings]);

  const value = {
    isBetaMode,
    betaLoading,
    toggleBetaMode,
    updateBetaSetting,
  };

  return (
    <BetaContext.Provider value={value}>
      {children}
    </BetaContext.Provider>
  );
};
